import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Certificate as CertificateIcon, ArrowLeft } from '@phosphor-icons/react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COURSE_INFO = {
  'powr': {
    title: 'POWR',
    subtitle: 'Purpose. Ownership. Wealth. Responsibility.',
    description: 'all four modules of the POWR course',
  },
  'ai-income': {
    title: 'AI Income Blueprint',
    subtitle: 'How to Use AI to Build Real Income Streams',
    description: 'all modules of the AI Income Blueprint course',
  },
  'inner-circle': {
    title: 'POWR Inner Circle',
    subtitle: 'EchoCard Income System',
    description: 'all modules of the POWR Inner Circle course',
  }
};

const CertificatePage = () => {
  const { userId } = useUser();
  const { courseId = 'powr' } = useParams();
  const navigate = useNavigate();
  const [completion, setCompletion] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progressData, setProgressData] = useState(null);

  const courseInfo = COURSE_INFO[courseId] || COURSE_INFO['powr'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [completionRes, progressRes] = await Promise.all([
          axios.get(`${API}/courses/${courseId}/completion`),
          axios.get(`${API}/progress/${userId}/course/${courseId}/summary`)
        ]);
        setCompletion(completionRes.data);
        setProgressData(progressRes.data);
        
        // Check if all modules completed
        if (progressRes.data.completed_modules === progressRes.data.total_modules) {
          // Try to fetch existing certificate
          try {
            const certRes = await axios.get(`${API}/certificate/${userId}`);
            // Check if certificate is for this course
            if (certRes.data && certRes.data.course_id === courseId) {
              setCertificate(certRes.data);
            }
          } catch (error) {
            // Certificate doesn't exist yet
          }
        } else {
          toast.error(`Complete all ${progressRes.data.total_modules} modules to get your certificate. You've completed ${progressRes.data.completed_modules}.`);
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, navigate, courseId]);

  const handleGenerateCertificate = async (e) => {
    e.preventDefault();
    if (!userName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/certificate`, {
        user_id: userId,
        user_name: userName,
        course_id: courseId
      });
      setCertificate(response.data);
      toast.success('Certificate generated! 🎉');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-mono text-[hsl(var(--text-muted))]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="container mx-auto max-w-4xl">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
            className="mb-8 rounded-none"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back Home
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-12">
              <CertificateIcon size={80} weight="duotone" className="mx-auto mb-6 text-[hsl(var(--accent))]" />
              <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
                {completion?.title}
              </h1>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg leading-relaxed whitespace-pre-wrap text-[hsl(var(--text))]">
                  {completion?.message}
                </p>
              </div>
            </div>

            {!certificate ? (
              <Card className="p-8 bg-[hsl(var(--card))] rounded-none border border-[hsl(var(--border))] max-w-md mx-auto">
                <h2 className="text-2xl font-black mb-6 text-center">Claim Your Certificate</h2>
                <form onSubmit={handleGenerateCertificate} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-2">
                      Enter Your Full Name
                    </label>
                    <Input
                      id="name"
                      data-testid="certificate-name-input"
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="John Doe"
                      className="rounded-none border-[hsl(var(--border))]"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="generate-certificate-btn"
                    disabled={generating}
                    className="w-full rounded-none bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--secondary))] transition-all active:scale-95"
                  >
                    {generating ? 'Generating...' : 'Generate Certificate'}
                  </Button>
                </form>
              </Card>
            ) : (
              <Card className="p-12 bg-[hsl(var(--surface))] rounded-none border-4 border-[hsl(var(--accent))] max-w-3xl mx-auto" data-testid="certificate-display">
                <div className="text-center space-y-6">
                  <div className="border-b-2 border-[hsl(var(--border))] pb-6">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-[hsl(var(--text-muted))] mb-4">
                      Certificate of Completion
                    </p>
                    <h2 className="text-5xl font-black tracking-tight mb-2">{courseInfo.title}</h2>
                    <p className="text-sm text-[hsl(var(--text-muted))]">
                      {courseInfo.subtitle}
                    </p>
                  </div>

                  <div className="py-8">
                    <p className="text-lg mb-4 text-[hsl(var(--text-muted))]">This certifies that</p>
                    <h3 className="text-4xl font-black mb-4">{certificate.user_name}</h3>
                    <p className="text-lg text-[hsl(var(--text-muted))]">
                      has successfully completed {courseInfo.description}
                    </p>
                  </div>

                  <div className="border-t-2 border-[hsl(var(--border))] pt-6">
                    <p className="font-mono text-xs text-[hsl(var(--text-muted))]">
                      Completed on {new Date(certificate.completion_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <p className="font-mono text-xs text-[hsl(var(--text-muted))] mt-2">
                      Certificate ID: {certificate.id}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CertificatePage;
